import { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { Window, TextField, Button, LoadingOverlay } from '@warmancer/ui';
import { authApi, ApiClientError, getErrorMessage, DISCORD_URL } from '../api/client';
import styles from './AuthPages.module.css';

export function ResetPassword() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [success, setSuccess] = useState(false);

  const resetMutation = useMutation<void, Error>({
    mutationFn: () => authApi.resetPassword({ token: token!, password }),
    onSuccess: () => {
      setSuccess(true);
    },
    onError: (error: Error) => {
      const message = getErrorMessage(error);
      if (error instanceof ApiClientError && error.field) {
        setErrors({ [error.field]: message });
      } else {
        setErrors({ form: message });
      }
    },
  });

  // Redirect if no token
  useEffect(() => {
    if (!token) {
      navigate('/forgot-password');
    }
  }, [token, navigate]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});

    const newErrors: Record<string, string> = {};

    if (!password) {
      newErrors.password = 'Password is required';
    } else if (password.length < 7) {
      newErrors.password = 'Password must be at least 7 characters';
    }

    if (password !== confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    resetMutation.mutate();
  };

  if (!token) {
    return null;
  }

  if (success) {
    return (
      <Window title="Password Reset" maxWidth={400} className={styles.window}>
        <div className={styles.successMessage}>
          <p>Your password has been successfully reset.</p>
          <p>You can now login with your new password.</p>
          <Link to="/login" className={styles.successLink}>
            Go to Login
          </Link>
        </div>
      </Window>
    );
  }

  return (
    <Window title="Set New Password" maxWidth={400} className={styles.window}>
      <form onSubmit={handleSubmit} className={styles.form}>
        {errors.form && (
          <div className={styles.formError}>
            {errors.form.split('\n').map((line, i) => <p key={i}>{line}</p>)}
            <p className={styles.helpContact}>Join us on <a href={DISCORD_URL} target="_blank" rel="noopener noreferrer">Discord</a> for help</p>
          </div>
        )}

        <TextField
          name="password"
          type="password"
          label="New Password"
          value={password}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPassword(e.target.value)}
          error={!!errors.password}
          autoComplete="new-password"
          autoFocus
        />

        <TextField
          name="confirmPassword"
          type="password"
          label="Confirm New Password"
          value={confirmPassword}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setConfirmPassword(e.target.value)}
          error={!!errors.confirmPassword}
          autoComplete="new-password"
        />

        <Button
          type="submit"
          disabled={resetMutation.isPending}
        >
          Reset Password
        </Button>
      </form>

      <LoadingOverlay
        visible={resetMutation.isPending}
        message="Resetting password..."
        timeout={10000}
      />
    </Window>
  );
}
